import React, { useState, useEffect, useCallback } from "react";
import {
  MessageCircle, Save, Eye, EyeOff, Send, CheckCircle2, Info,
  ExternalLink, Link2, Phone, Settings, RefreshCw, Unplug, CheckCheck,
  FileText, Share2, Lock, Search, Sparkles, Calendar, X, Pencil, RotateCcw,
} from "lucide-react";
import { Form, Row, Col, Spinner, Badge } from "react-bootstrap";
import { PageHeader, AppButton, ConfirmModal, Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import CreateTemplateForm from "../../WhatsApp/CreateTemplateForm";
import "../Settings/settings.scss";
import "./PredefinedLibrary.scss";

/**
 * WhatsAppConfigPage — Platform-level WhatsApp configuration.
 * Two sections:
 *   1. Meta App Credentials (appId, appSecret) — needed for ALL institute Embedded Signups
 *   2. Connect Number — Embedded Signup for the SA's own WhatsApp number
 */
const WhatsAppConfigPage = () => {
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const tabs = [
    { id: "credentials", label: "Meta Credentials", icon: Settings },
    { id: "connect", label: "Connect Number", icon: Phone },
    { id: "templates", label: "Platform Templates", icon: FileText },
    { id: "library", label: "Predefined Library", icon: Sparkles },
  ];
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      const h = window.location.hash.replace("#", "");
      if (tabs.some((t) => t.id === h)) return h;
    }
    return "credentials";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${activeTab}`);
    }
  }, [activeTab]);

  return (
    <div className="sa-settings">
      <PageHeader
        title="WhatsApp Configuration"
        description="Configure Meta App credentials and connect your platform WhatsApp number"
        icon={MessageCircle}
      />

      {/* ── Tabbed sections ───────────────────────────────────────── */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabsList>
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id}>
                <Icon size={15} />
                <span className="ms-1">{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        <TabsContent value="credentials"><MetaAppCredentials showToast={showToast} /></TabsContent>
        <TabsContent value="connect"><PlatformConnectSection showToast={showToast} /></TabsContent>
        <TabsContent value="templates"><PlatformTemplatesSection showToast={showToast} /></TabsContent>
        <TabsContent value="library"><PredefinedLibrarySection showToast={showToast} /></TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/* ─── Meta App Credentials ─────────────────────────────────────────────────── */
const MetaAppCredentials = ({ showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [form, setForm] = useState({ appId: "", appSecret: "", hasAppSecret: false });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/sa/whatsapp");
        setForm({ appId: data.appId || "", appSecret: data.appSecret || "", hasAppSecret: data.hasAppSecret || false });
      } catch { /* defaults */ }
      setLoading(false);
    })();
  }, []);

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const toggleSecret = (field) => setShowSecrets((p) => ({ ...p, [field]: !p[field] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/sa/whatsapp", { appId: form.appId, appSecret: form.appSecret });
      showToast("Meta App credentials saved!");
      const { data } = await apiClient.get("/sa/whatsapp");
      setForm({ appId: data.appId || "", appSecret: data.appSecret || "", hasAppSecret: data.hasAppSecret || false });
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save.", "error");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="d-flex justify-content-center p-4"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div className="sa-card">
      <div className="sa-card-head">
        <Settings size={16} className="me-2" /> Meta App Credentials
        <AppButton variant="primary" size="sm" onClick={handleSave} disabled={saving} style={{ marginLeft: "auto" }}>
          <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save"}
        </AppButton>
      </div>
      <div className="sa-card-body">
        <div style={{ fontSize: "0.78rem", color: "var(--text-tertiary, #9ca3af)", lineHeight: 1.5, marginBottom: "1rem" }}>
          <Info size={13} className="me-1" style={{ position: "relative", top: -1 }} />
          These credentials are required for Embedded Signup (both platform and institute connections). Get them from your{" "}
          <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">
            Meta App Dashboard <ExternalLink size={11} />
          </a>.
        </div>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="sa-settings__label">Meta App ID</Form.Label>
              <Form.Control size="sm" value={form.appId} onChange={update("appId")} placeholder="e.g. 123456789012345" />
              <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>Settings → Basic → App ID</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="sa-settings__label">
                App Secret
                {form.hasAppSecret && <CheckCircle2 size={13} className="ms-1 text-success" />}
              </Form.Label>
              <div className="d-flex gap-1">
                <Form.Control
                  size="sm"
                  type={showSecrets.appSecret ? "text" : "password"}
                  value={form.appSecret}
                  onChange={update("appSecret")}
                  placeholder="Enter app secret…"
                />
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleSecret("appSecret")} style={{ padding: "0.25rem 0.5rem" }}>
                  {showSecrets.appSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>Settings → Basic → App Secret</Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </div>
    </div>
  );
};

/* ─── Platform Connect Section (Embedded Signup) ──────────────────────────── */
const PlatformConnectSection = ({ showToast }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Test message state
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/sa/whatsapp/connect/status");
      setStatus(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleConnect = async () => {
    try {
      const { data } = await apiClient.get("/sa/whatsapp/connect/config");
      if (!data.configured || !data.appId) {
        showToast("Save Meta App ID & App Secret first before connecting.", "error");
        return;
      }
      const scope = "business_management,whatsapp_business_management,whatsapp_business_messaging";
      // Reuse the whitelisted redirect URI; pass state=platform so callback knows to use SA endpoint
      const redirectUri = data.redirectUri || `${window.location.origin}/whatsapp/connect/callback`;
      const url = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${data.appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&response_type=code&state=platform`;
      window.open(url, "meta_wa_platform_signup", "width=600,height=700,scrollbars=yes");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load connect config.", "error");
    }
  };

  const confirmDisconnect = async () => {
    setShowDisconnectConfirm(false);
    setDisconnecting(true);
    try {
      await apiClient.post("/sa/whatsapp/disconnect");
      showToast("WhatsApp disconnected");
      setStatus({ connected: false });
    } catch (err) {
      showToast(err?.response?.data?.message || "Disconnect failed", "error");
    }
    setDisconnecting(false);
  };

  const handleRepairWebhook = async () => {
    setRepairing(true);
    try {
      const { data } = await apiClient.post("/sa/whatsapp/connect/repair-webhook");
      showToast(data.message || "Webhook repaired");
    } catch (err) {
      showToast(err?.response?.data?.message || "Repair failed", "error");
    }
    setRepairing(false);
  };

  const handleTest = async () => {
    if (!testPhone.trim()) { showToast("Enter a phone number.", "warning"); return; }
    setTesting(true);
    try {
      const { data } = await apiClient.post("/sa/whatsapp/test", { testPhone: testPhone.trim() });
      showToast(data.message || "Test message sent!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Test failed.", "error");
    }
    setTesting(false);
  };

  if (loading) {
    return <div className="d-flex justify-content-center p-4"><Spinner animation="border" size="sm" /></div>;
  }

  /* ── Not connected ── */
  if (!status?.connected) {
    return (
      <div className="sa-card">
        <div className="sa-card-head">
          <Phone size={16} className="me-2" /> Platform WhatsApp Number
        </div>
        <div className="sa-card-body">
          <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
            <div style={{ marginBottom: "1rem" }}>
              <MessageCircle size={40} style={{ color: "var(--text-tertiary, #9ca3af)" }} />
            </div>
            <h5 style={{ marginBottom: "0.5rem" }}>No WhatsApp Number Connected</h5>
            <p style={{ fontSize: "0.85rem", color: "var(--text-tertiary, #9ca3af)", maxWidth: 400, margin: "0 auto 1.5rem" }}>
              Connect your platform WhatsApp Business number via Meta Embedded Signup
              to receive messages in the Platform WhatsApp Inbox.
            </p>
            <AppButton onClick={handleConnect}>
              <Link2 size={16} /> Connect WhatsApp
            </AppButton>
          </div>
        </div>
      </div>
    );
  }

  /* ── Connected ── */
  return (
    <>
      <div className="sa-card">
        <div className="sa-card-head">
          <Phone size={16} className="me-2" /> Platform WhatsApp Number
        </div>
        <div className="sa-card-body">
          <div style={{ padding: "0.5rem 0.75rem", marginBottom: "1rem", borderRadius: 6, background: "var(--color-success-bg, #dcfce7)", color: "var(--color-success, #16a34a)", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCheck size={14} />
            WhatsApp connected{status.connectedVia === "embedded_signup" ? " via Meta Embedded Signup" : ""}.
          </div>

          <Row className="g-3 mb-3">
            <Col md={3} sm={6}>
              <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 2 }}>Phone Number</div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{status.displayPhoneNumber || status.phoneNumberId}</div>
            </Col>
            <Col md={3} sm={6}>
              <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 2 }}>Quality</div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{status.qualityRating || "N/A"}</div>
            </Col>
            <Col md={3} sm={6}>
              <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 2 }}>Messaging Tier</div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{status.messagingLimit ? status.messagingLimit.replace("TIER_", "").replace("K", "K/day") : "N/A"}</div>
            </Col>
            <Col md={3} sm={6}>
              <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 2 }}>Connected</div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{status.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : "N/A"}</div>
            </Col>
          </Row>

          <Row className="g-3 mb-3" style={{ fontSize: "0.82rem" }}>
            <Col md={6}>
              <span style={{ color: "var(--text-tertiary)" }}>WABA ID:</span> {status.wabaId}
            </Col>
            <Col md={6}>
              <span style={{ color: "var(--text-tertiary)" }}>Phone Number ID:</span> {status.phoneNumberId}
            </Col>
          </Row>

          <div className="d-flex flex-wrap gap-2">
            <AppButton variant="outline" size="sm" onClick={handleRepairWebhook} disabled={repairing}>
              <RefreshCw size={14} className={repairing ? "wa-spin" : ""} />
              {repairing ? "Repairing…" : "Repair Webhook"}
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={handleConnect}>
              <Link2 size={14} /> Reconnect
            </AppButton>
            <AppButton variant="destructive" size="sm" onClick={() => setShowDisconnectConfirm(true)} disabled={disconnecting}>
              <Unplug size={14} /> {disconnecting ? "Disconnecting…" : "Disconnect"}
            </AppButton>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="sa-card">
        <div className="sa-card-head">
          <Send size={16} className="me-2" /> Test Connection
        </div>
        <div className="sa-card-body">
          <Row className="g-3 align-items-end">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Phone Number (with country code)</Form.Label>
                <Form.Control size="sm" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="e.g. 919876543210" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <AppButton variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                {testing ? <><Spinner animation="border" size="sm" className="me-1" /> Sending…</> : <><Send size={14} className="me-1" /> Send Test</>}
              </AppButton>
            </Col>
          </Row>
        </div>
      </div>

      <ConfirmModal
        show={showDisconnectConfirm}
        onHide={() => setShowDisconnectConfirm(false)}
        onConfirm={confirmDisconnect}
        title="Disconnect WhatsApp"
        message="Are you sure? The platform inbox will stop receiving messages."
        confirmText="Disconnect"
        variant="danger"
      />
    </>
  );
};

/* ─── Shared template-preview helpers ──────────────────────────────────────
 * Per Meta WhatsApp Cloud API, every BODY/HEADER component containing
 * positional variables ({{1}}, {{2}}, …) must include an `example` field so
 * Meta reviewers know what real content looks like. We surface those examples
 * in the UI in two ways:
 *   1. The bubble preview substitutes {{n}} with the actual example value
 *      (rendered as a highlighted span so reviewers know it's a placeholder).
 *   2. A small legend below the bubble lists each {{n}} → example pair.
 * ──────────────────────────────────────────────────────────────────────── */
const _getBodyExamples = (components) => {
  const body = (components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  // Meta format: example.body_text is a 2D array; first row = positional values
  const row = body?.example?.body_text?.[0];
  return Array.isArray(row) ? row : [];
};

// Render text containing {{1}}, {{2}}, … as React nodes, replacing each
// placeholder with the example value wrapped in a highlighted <span>. WhatsApp's
// own `*bold*` markdown is also honoured (very lightweight — single segment).
const renderTextWithExamples = (text, examples) => {
  if (!text) return null;
  const parts = text.split(/(\{\{\d+\}\})/g);
  return parts.map((p, i) => {
    const m = /^\{\{(\d+)\}\}$/.exec(p);
    if (m) {
      const idx = parseInt(m[1], 10) - 1;
      const val = examples[idx];
      if (val !== undefined && val !== null && val !== "") {
        return (
          <span key={i} className="wa-lib__var">{val}</span>
        );
      }
      return <span key={i} className="wa-lib__var">{p}</span>;
    }
    // Lightweight WhatsApp *bold* support
    const segs = p.split(/(\*[^*\n]+\*)/g);
    return segs.map((s, j) => {
      if (/^\*[^*\n]+\*$/.test(s)) {
        return <strong key={`${i}-${j}`}>{s.slice(1, -1)}</strong>;
      }
      return <React.Fragment key={`${i}-${j}`}>{s}</React.Fragment>;
    });
  });
};

// Inline WhatsApp-styled chat preview used by every template card / modal.
// Theme-aware via CSS vars defined in PredefinedLibrary.scss.
const WhatsAppPreview = ({ components, showLegend = false }) => {
  const body = (components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  const footer = (components || []).find((c) => (c.type || "").toUpperCase() === "FOOTER");
  const buttons = (components || []).find((c) => (c.type || "").toUpperCase() === "BUTTONS");
  const examples = _getBodyExamples(components);
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      <div className="wa-lib__preview">
        <div className="wa-lib__bubble">
          {renderTextWithExamples(body?.text || "(no body)", examples)}
          {footer?.text && (
            <div className="wa-lib__bubble-footer">{footer.text}</div>
          )}
          <span className="wa-lib__bubble-time">{time} ✓✓</span>
          <span className="wa-lib__bubble-tail" aria-hidden="true" />
        </div>
        {buttons?.buttons?.length > 0 && (
          <div className="wa-lib__buttons">
            {buttons.buttons.map((b, i) => (
              <div key={i} className="wa-lib__button">
                {b.type === "URL" ? "🔗 " : "💬 "}{b.text}
              </div>
            ))}
          </div>
        )}
      </div>
      {showLegend && examples.length > 0 && (
        <VariableLegend body={body} />
      )}
    </>
  );
};

const VariableLegend = ({ body }) => {
  const text = body?.text || "";
  const examples = body?.example?.body_text?.[0] || [];
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  const uniqueVars = Array.from(new Set(matches));
  if (!uniqueVars.length) return null;
  return (
    <div className="wa-lib__legend">
      <div className="wa-lib__legend-title">
        <Info size={11} /> Variable example values
      </div>
      {uniqueVars.map((v) => {
        const idx = parseInt(v.replace(/[{}]/g, ""), 10) - 1;
        const val = examples[idx];
        return (
          <div key={v} className="wa-lib__legend-row">
            <span className="wa-lib__legend-key">{v}</span>
            <span className="wa-lib__legend-val">
              {val !== undefined && val !== "" ? val : <em style={{ color: "var(--color-warning)" }}>missing — Meta will reject</em>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Platform Templates Section ───────────────────────────────────────── */
const PlatformTemplatesSection = ({ showToast }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState({});
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/platform/whatsapp/templates");
      setTemplates(data.templates || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/platform/whatsapp/templates/sync");
      showToast(data.message || "Templates synced");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const handleToggleShare = async (tplName, currentShared) => {
    setToggling((p) => ({ ...p, [tplName]: true }));
    try {
      await apiClient.patch(`/platform/whatsapp/templates/${encodeURIComponent(tplName)}/share`, {
        isShared: !currentShared,
      });
      setTemplates((prev) =>
        prev.map((t) => (t.name === tplName ? { ...t, isShared: !currentShared } : t))
      );
      showToast(`Template "${tplName}" ${!currentShared ? "shared with institutes" : "set to private"}`);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update sharing", "error");
    }
    setToggling((p) => ({ ...p, [tplName]: false }));
  };

  // Group by name (one name can have multiple languages, take first)
  const grouped = {};
  templates.forEach((t) => {
    if (!grouped[t.name]) grouped[t.name] = t;
  });
  const uniqueTemplates = Object.values(grouped);

  const filtered = uniqueTemplates.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const sharedCount = uniqueTemplates.filter((t) => t.isShared).length;

  const _getBodyText = (components) => {
    const body = (components || []).find((c) => c.type === "BODY");
    return body?.text || "";
  };

  const statusColor = (s) => {
    if (s === "APPROVED") return "success";
    if (s === "PENDING") return "warning";
    if (s === "REJECTED") return "danger";
    return "secondary";
  };

  return (
    <div className="sa-card">
      <div className="sa-card-head">
        <FileText size={16} className="me-2" /> Platform Templates
        <span style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", marginLeft: 8 }}>
          {sharedCount} shared / {uniqueTemplates.length} total
        </span>
        <AppButton variant="outline" size="sm" onClick={handleSync} disabled={syncing} style={{ marginLeft: "auto" }}>
          <RefreshCw size={14} className={syncing ? "wa-spin" : ""} />
          {syncing ? " Syncing…" : " Sync from Meta"}
        </AppButton>
      </div>
      <div className="sa-card-body">
        <div style={{ fontSize: "0.78rem", color: "var(--text-tertiary)", lineHeight: 1.5, marginBottom: "1rem" }}>
          <Info size={13} className="me-1" style={{ position: "relative", top: -1 }} />
          Toggle sharing to make templates available as <strong>Recommended Templates</strong> for all institutes.
          Private templates remain accessible only from the platform inbox.
        </div>

        {loading ? (
          <div className="d-flex justify-content-center p-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : uniqueTemplates.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem 1rem", color: "var(--text-tertiary)" }}>
            <FileText size={36} style={{ opacity: 0.4, marginBottom: "0.5rem" }} />
            <p style={{ fontSize: "0.85rem" }}>No platform templates yet. Click "Sync from Meta" to import.</p>
          </div>
        ) : (
          <>
            {uniqueTemplates.length > 5 && (
              <div style={{ marginBottom: "0.75rem", position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: 10, top: 9, color: "var(--text-tertiary)" }} />
                <Form.Control
                  size="sm"
                  placeholder="Search templates…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 30, fontSize: "0.8rem" }}
                />
              </div>
            )}

            <div className="wa-lib__grid">
              {filtered.map((tpl) => {
                const sharedClass = ["wa-lib__card", tpl.isShared && "wa-lib__card--shared"]
                  .filter(Boolean).join(" ");
                return (
                  <div key={tpl.name} className={sharedClass} style={{ cursor: "default" }}>
                    {/* Header: name + status */}
                    <div className="wa-lib__header">
                      <div className="wa-lib__header-meta">
                        <div className="wa-lib__name">{tpl.name}</div>
                        <div className="wa-lib__desc">
                          {tpl.language?.toUpperCase()} • {tpl.category || "—"}
                        </div>
                      </div>
                      <Badge bg={statusColor(tpl.status)} style={{ fontSize: "0.62rem" }}>
                        {tpl.status}
                      </Badge>
                    </div>

                    {/* WhatsApp-styled preview with example values substituted */}
                    <WhatsAppPreview components={tpl.components} />

                    {/* Variable legend (so SA knows what each {{n}} means) */}
                    <VariableLegend
                      body={(tpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY")}
                    />

                    {/* Share toggle row */}
                    <div className="wa-lib__chips">
                      <span
                        className={`wa-lib__share ${tpl.isShared ? "wa-lib__share--on" : ""}`}
                        style={{ marginLeft: 0 }}
                      >
                        {tpl.isShared ? <Share2 size={12} /> : <Lock size={12} />}
                        <Form.Check
                          type="switch"
                          id={`share-${tpl.name}-${tpl.language}`}
                          checked={!!tpl.isShared}
                          disabled={!!toggling[tpl.name]}
                          onChange={() => handleToggleShare(tpl.name, tpl.isShared)}
                          label={tpl.isShared ? "Shared with institutes" : "Private"}
                        />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {filtered.length === 0 && search && (
              <div style={{ textAlign: "center", padding: "1rem", color: "var(--text-tertiary)", fontSize: "0.82rem" }}>
                No templates match "{search}"
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Predefined Library Section ───────────────────────────────────────────
 * Curated daily-life + Indian festival templates that the SaaS admin can
 * bulk-submit to Meta in one click. Once approved, they auto-appear in the
 * platform templates list above and (with autoShare) become "Recommended
 * Templates" for institutes.
 * ──────────────────────────────────────────────────────────────────────── */
const PredefinedLibrarySection = ({ showToast }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autoShare, setAutoShare] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | daily | festival | new
  const [previewTpl, setPreviewTpl] = useState(null);
  const [showResult, setShowResult] = useState(null);
  // SA-side edits to predefined templates. Stored in-memory only and sent to BE
  // as `overrides` on submit. Map of templateName -> { category, language, components }.
  const [overrides, setOverrides] = useState({});
  const [editingTpl, setEditingTpl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/platform/whatsapp/templates/predefined");
      setTemplates(data.templates || []);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load library", "error");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const isFestival = (name) => /diwali|holi|new_year|republic|independence|raksha|ganesh|navratri|dussehra|eid|christmas|pongal|teachers|childrens|womens|guru_purnima|lohri|onam|ugadi|gudi/.test(name);

  const filteredTemplates = templates.filter((t) => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) &&
        !(t.description || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === "new" && t.existingStatus) return false;
    if (filter === "daily" && isFestival(t.name)) return false;
    if (filter === "festival" && !isFestival(t.name)) return false;
    return true;
  });

  const newCount = templates.filter((t) => !t.existingStatus).length;
  const sharedCount = templates.filter((t) => t.isShared).length;

  const selectAllVisible = () => {
    const next = new Set(selected);
    filteredTemplates.forEach((t) => { if (!t.existingStatus) next.add(t.name); });
    setSelected(next);
  };
  const clearSelection = () => setSelected(new Set());

  const submit = async (subset) => {
    setSubmitting(true);
    try {
      const body = { autoShare };
      if (subset && subset.length) body.only = subset;
      // Only send overrides for templates being submitted in this call
      const submittingNames = subset && subset.length
        ? subset
        : templates.filter((t) => !t.existingStatus).map((t) => t.name);
      const relevantOverrides = {};
      submittingNames.forEach((n) => {
        if (overrides[n]) relevantOverrides[n] = overrides[n];
      });
      if (Object.keys(relevantOverrides).length) body.overrides = relevantOverrides;
      const { data } = await apiClient.post("/platform/whatsapp/templates/seed-predefined", body);
      setShowResult(data);
      showToast(`${data.summary?.submitted || 0} submitted • ${data.summary?.skipped || 0} skipped • ${data.summary?.failed || 0} failed`);
      clearSelection();
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Bulk submit failed", "error");
    }
    setSubmitting(false);
  };

  const handleEditSave = (tplName, formData) => {
    // formData is the full Meta-spec body the form would have submitted:
    // { name, language, category, components }
    setOverrides((prev) => ({
      ...prev,
      [tplName]: {
        category: formData.category,
        language: formData.language,
        components: formData.components,
      },
    }));
    setEditingTpl(null);
    showToast(`Edited "${tplName}". Submit to apply.`);
  };

  const handleResetEdit = (tplName) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[tplName];
      return next;
    });
    showToast(`Reset "${tplName}" to original.`);
  };

  const statusColor = (s) => {
    if (s === "APPROVED") return "success";
    if (s === "PENDING") return "warning";
    if (s === "REJECTED") return "danger";
    if (!s) return "secondary";
    return "secondary";
  };

  const _bodyText = (components) => {
    const body = (components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
    return body?.text || "";
  };

  return (
    <div className="sa-card">
      <div className="sa-card-head">
        <Sparkles size={16} className="me-2" /> Predefined Template Library
        <span style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", marginLeft: 8 }}>
          {templates.length} curated • {newCount} new • {sharedCount} shared
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <Form.Check
            type="switch"
            id="auto-share-switch"
            label="Auto-share on approval"
            checked={autoShare}
            onChange={(e) => setAutoShare(e.target.checked)}
            style={{ fontSize: "0.78rem", marginBottom: 0 }}
          />
          <AppButton
            variant="primary"
            size="sm"
            onClick={() => submit(selected.size ? Array.from(selected) : null)}
            disabled={submitting || loading || (newCount === 0 && selected.size === 0)}
          >
            {submitting ? <Spinner size="sm" animation="border" /> : <Send size={14} />}
            {selected.size > 0
              ? ` Submit ${selected.size} selected`
              : ` Submit all new (${newCount})`}
          </AppButton>
        </div>
      </div>
      <div className="sa-card-body">
        <div style={{ fontSize: "0.78rem", color: "var(--text-tertiary)", lineHeight: 1.5, marginBottom: "0.85rem" }}>
          <Info size={13} className="me-1" style={{ position: "relative", top: -1 }} />
          One-click submit to Meta for approval. Already-approved or pending templates are automatically skipped.
          With <strong>Auto-share on approval</strong> enabled, approved templates will instantly appear under
          "Recommended Templates" for institutes.
        </div>

        {/* Approval status legend */}
        <div className="wa-lib__legend" style={{ marginBottom: "0.85rem" }}>
          <div className="wa-lib__legend-title">
            <Info size={11} /> How template approval works
          </div>
          <div className="wa-lib__legend-row">
            <Badge bg="warning" style={{ minWidth: 80, textAlign: "center" }}>PENDING ⏳</Badge>
            <span className="wa-lib__legend-val">Submitted to Meta — review usually takes 1 minute to a few hours.</span>
          </div>
          <div className="wa-lib__legend-row">
            <Badge bg="success" style={{ minWidth: 80, textAlign: "center" }}>APPROVED ✓</Badge>
            <span className="wa-lib__legend-val">Live and ready to send. Status auto-updates via Meta webhook.</span>
          </div>
          <div className="wa-lib__legend-row">
            <Badge bg="danger" style={{ minWidth: 80, textAlign: "center" }}>REJECTED ✗</Badge>
            <span className="wa-lib__legend-val">Rejection reason shown on the card. Edit the template and resubmit.</span>
          </div>
          <div className="wa-lib__legend-row">
            <Badge bg="secondary" style={{ minWidth: 80, textAlign: "center" }}>—</Badge>
            <span className="wa-lib__legend-val">Not yet submitted. Tick the card and click "Submit selected" above.</span>
          </div>
          <div className="wa-lib__legend-row" style={{ marginTop: 4 }}>
            <span className="wa-lib__legend-key">Auto-sync</span>
            <span className="wa-lib__legend-val">
              Statuses refresh in real-time via Meta webhook, plus a daily sync at 3 AM IST.
              Use <strong>"Sync from Meta"</strong> in the Platform Templates tab to force a refresh anytime.
            </span>
          </div>
        </div>

        {/* Filter tabs + search */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: "0.75rem", alignItems: "center" }}>
          {[
            { id: "all", label: "All" },
            { id: "new", label: `New (${newCount})` },
            { id: "daily", label: "Daily" },
            { id: "festival", label: "Festivals" },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              style={{
                fontSize: "0.74rem",
                padding: "4px 10px",
                borderRadius: 999,
                border: "1px solid var(--border-color, #e5e7eb)",
                background: filter === f.id ? "var(--brand-primary, #2563eb)" : "transparent",
                color: filter === f.id ? "#fff" : "var(--text-secondary)",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {f.label}
            </button>
          ))}
          <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 9, color: "var(--text-tertiary)" }} />
            <Form.Control
              size="sm"
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30, fontSize: "0.8rem" }}
            />
          </div>
          {selected.size > 0 ? (
            <AppButton variant="ghost" size="sm" onClick={clearSelection}>
              <X size={13} /> Clear ({selected.size})
            </AppButton>
          ) : (
            <AppButton variant="ghost" size="sm" onClick={selectAllVisible} disabled={!filteredTemplates.some((t) => !t.existingStatus)}>
              Select all new
            </AppButton>
          )}
        </div>

        {loading ? (
          <div className="d-flex justify-content-center p-4"><Spinner animation="border" size="sm" /></div>
        ) : filteredTemplates.length === 0 ? (
          <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-tertiary)", fontSize: "0.85rem" }}>
            No templates match the current filter.
          </div>
        ) : (
          <div className="wa-lib__grid">
            {filteredTemplates.map((rawTpl) => {
              // Apply SA-side override (if any) so preview + variable legend
              // reflect pending edits before submission.
              const ov = overrides[rawTpl.name];
              const tpl = ov
                ? {
                    ...rawTpl,
                    category: ov.category || rawTpl.category,
                    language: ov.language || rawTpl.language,
                    components: ov.components || rawTpl.components,
                  }
                : rawTpl;
              const isEdited = !!ov;
              const alreadyExists = !!tpl.existingStatus;
              const isSelected = selected.has(tpl.name);
              const body = (tpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
              const variableCount = (body?.text?.match(/\{\{\d+\}\}/g) || []).length;
              const cardClass = [
                "wa-lib__card",
                isSelected && "wa-lib__card--selected",
                alreadyExists && "wa-lib__card--disabled",
                isEdited && "wa-lib__card--edited",
              ].filter(Boolean).join(" ");

              return (
                <div
                  key={tpl.name}
                  className={cardClass}
                  onClick={() => !alreadyExists && toggleSelect(tpl.name)}
                >
                  {/* Header strip */}
                  <div className="wa-lib__header">
                    <Form.Check
                      type="checkbox"
                      checked={isSelected}
                      disabled={alreadyExists}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(tpl.name); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="wa-lib__header-meta">
                      <div className="wa-lib__name">{tpl.name}</div>
                      <div className="wa-lib__desc">
                        {tpl.description || `${variableCount} variable${variableCount !== 1 ? "s" : ""}`}
                      </div>
                    </div>
                    {/* Edit / Reset actions — disabled for already-submitted templates
                        because Meta requires deletion + recreation to change content. */}
                    {!alreadyExists && (
                      <div className="wa-lib__card-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="wa-lib__icon-btn"
                          title="Edit template"
                          onClick={() => setEditingTpl(tpl)}
                        >
                          <Pencil size={13} />
                        </button>
                        {isEdited && (
                          <button
                            type="button"
                            className="wa-lib__icon-btn"
                            title="Reset to original"
                            onClick={() => handleResetEdit(tpl.name)}
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* WhatsApp-styled chat preview (theme-aware, with example values) */}
                  <WhatsAppPreview components={tpl.components} />

                  {/* Variable legend so SA sees what each {{n}} maps to */}
                  <VariableLegend body={body} />

                  {/* Footer chips */}
                  <div className="wa-lib__chips">
                    <Badge bg={tpl.category === "MARKETING" ? "info" : "primary"}>
                      {tpl.category}
                    </Badge>
                    {isFestival(tpl.name) && (
                      <Badge bg="light" text="dark">
                        <Calendar size={9} /> Festival
                      </Badge>
                    )}
                    {variableCount > 0 && (
                      <Badge bg="light" text="dark">
                        {variableCount} var{variableCount !== 1 ? "s" : ""}
                      </Badge>
                    )}
                    {isEdited && (
                      <Badge bg="warning" text="dark" title="Edited locally — submit to apply">
                        <Pencil size={9} /> Edited
                      </Badge>
                    )}
                    {alreadyExists && (
                      <Badge bg={statusColor(tpl.existingStatus)} title={tpl.rejectedReason || ""}>
                        {tpl.existingStatus}
                        {tpl.existingStatus === "PENDING" && " ⏳"}
                        {tpl.existingStatus === "APPROVED" && " ✓"}
                        {tpl.existingStatus === "REJECTED" && " ✗"}
                      </Badge>
                    )}
                    {tpl.isShared && (
                      <Badge bg="success">
                        <Share2 size={8} /> Shared
                      </Badge>
                    )}
                  </div>

                  {/* Submission / sync timestamps */}
                  {(tpl.submittedAt || tpl.syncedAt || tpl.rejectedReason) && (
                    <div className="wa-lib__legend" style={{ margin: "0 10px 10px" }}>
                      {tpl.submittedAt && (
                        <div className="wa-lib__legend-row">
                          <span className="wa-lib__legend-key">Submitted</span>
                          <span className="wa-lib__legend-val">
                            {new Date(tpl.submittedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {tpl.syncedAt && (
                        <div className="wa-lib__legend-row">
                          <span className="wa-lib__legend-key">Last sync</span>
                          <span className="wa-lib__legend-val">
                            {new Date(tpl.syncedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {tpl.rejectedReason && (
                        <div className="wa-lib__legend-row">
                          <span className="wa-lib__legend-key" style={{ color: "var(--color-danger)" }}>
                            Reason
                          </span>
                          <span className="wa-lib__legend-val">{tpl.rejectedReason}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewTpl && (
        <PreviewModal tpl={previewTpl} onClose={() => setPreviewTpl(null)} />
      )}

      {/* Edit modal — uses the same CreateTemplateForm as the tenant flow,
          pre-filled with the (possibly already-edited) template. */}
      {editingTpl && (
        <div onClick={() => setEditingTpl(null)} className="wa-lib__modal-backdrop">
          <div onClick={(e) => e.stopPropagation()} className="wa-lib__modal wa-lib__modal--lg">
            <div className="wa-lib__modal-head">
              <h5><Pencil size={14} /> Edit predefined template: {editingTpl.name}</h5>
              <button onClick={() => setEditingTpl(null)} className="wa-lib__modal-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="wa-lib__legend" style={{ marginBottom: "0.85rem" }}>
              <Info size={11} /> Edits are kept locally until you submit. They never modify the
              source library file. The edited version goes to Meta when you submit this template.
            </div>
            <CreateTemplateForm
              initialValues={editingTpl}
              submitLabel="Save edits"
              onSubmit={async (formData) => handleEditSave(editingTpl.name, formData)}
              onCancel={() => setEditingTpl(null)}
            />
          </div>
        </div>
      )}

      {/* Result modal */}
      {showResult && (
        <ResultModal result={showResult} onClose={() => setShowResult(null)} />
      )}
    </div>
  );
};

const PreviewModal = ({ tpl, onClose }) => {
  return (
    <div onClick={onClose} className="wa-lib__modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="wa-lib__modal">
        <div className="wa-lib__modal-head">
          <h5>{tpl.name}</h5>
          <button onClick={onClose} className="wa-lib__modal-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div style={{ marginBottom: 8 }}>
          <Badge bg={tpl.category === "MARKETING" ? "info" : "primary"} className="me-2">{tpl.category}</Badge>
          <Badge bg="light" text="dark">{tpl.language}</Badge>
        </div>
        {tpl.description && (
          <div className="wa-lib__modal-section">{tpl.description}</div>
        )}

        {/* WhatsApp-styled preview pane (theme-aware) */}
        <div className="wa-lib__modal-preview">
          <WhatsAppPreview components={tpl.components} />
        </div>

        {/* Variable legend with example values */}
        <VariableLegend
          body={(tpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY")}
        />
      </div>
    </div>
  );
};

const ResultModal = ({ result, onClose }) => (
  <div onClick={onClose} className="wa-lib__modal-backdrop">
    <div onClick={(e) => e.stopPropagation()} className="wa-lib__modal" style={{ maxWidth: 520 }}>
      <div className="wa-lib__modal-head">
        <h5>Submission Result</h5>
        <button onClick={onClose} className="wa-lib__modal-close" aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <Badge bg="success">{result.summary?.submitted || 0} submitted</Badge>
        <Badge bg="secondary">{result.summary?.skipped || 0} skipped</Badge>
        {result.summary?.failed > 0 && <Badge bg="danger">{result.summary.failed} failed</Badge>}
      </div>
      {result.failed?.length > 0 && (
        <div className="wa-lib__modal-section">
          <strong>Failed:</strong>
          <ul style={{ paddingLeft: 20, marginTop: 4 }}>
            {result.failed.map((f, i) => (
              <li key={i}><code>{f.name}</code>: {f.error}{f.detail ? ` — ${f.detail}` : ""}</li>
            ))}
          </ul>
        </div>
      )}
      {result.skipped?.length > 0 && (
        <div className="wa-lib__modal-section">
          <strong>Skipped:</strong>
          <ul style={{ paddingLeft: 20, marginTop: 4 }}>
            {result.skipped.map((s, i) => (
              <li key={i}><code>{s.name}</code>: {s.reason}</li>
            ))}
          </ul>
        </div>
      )}
      {result.submitted?.length > 0 && (
        <div className="wa-lib__modal-section">
          <strong>Submitted to Meta:</strong>
          <ul style={{ paddingLeft: 20, marginTop: 4 }}>
            {result.submitted.map((s, i) => (
              <li key={i}><code>{s.name}</code> — <Badge bg="warning">{s.status}</Badge></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

export default WhatsAppConfigPage;
